// src/pages/InviteEmployeesPage.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mentoringApi } from '../axiosInstance';
import { ensureMentoringSession } from '../lib/session';

const ALLOWED_ROLES = ['nl_manager', 'hr', 'hr_admin'];

const fmtDE = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function InviteEmployeesPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(true);

  // Single invite
  const [singleForm, setSingleForm] = useState({ email: '', first_name: '', last_name: '' });
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleResult, setSingleResult] = useState(null); // { type, text }

  // CSV
  const [csvParsed, setCsvParsed] = useState(null); // Array of { email, first_name, last_name, error? }
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  // Invitation list
  const [invitations, setInvitations] = useState([]);
  const [listLoading, setListLoading] = useState(true);

  // Session guard
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const whoami = await ensureMentoringSession();
        const u = whoami?.user || whoami;
        if (!alive) return;
        if (!u || !ALLOWED_ROLES.includes(u.role)) {
          navigate('/nl-login', { replace: true });
          return;
        }
      } catch {
        if (alive) navigate('/nl-login', { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  // Load invitation list
  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await mentoringApi.get('/mpi/invite-employees/list', { withCredentials: true });
      setInvitations(res.data?.invitations || []);
    } catch { /* silent */ }
    setListLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  // ── Single invite ──
  async function handleSingleInvite(e) {
    e.preventDefault();
    if (!singleForm.email.trim() || singleLoading) return;
    setSingleLoading(true);
    setSingleResult(null);
    try {
      const res = await mentoringApi.post('/mpi/invite-employees', {
        employees: [{ email: singleForm.email.trim(), first_name: singleForm.first_name.trim(), last_name: singleForm.last_name.trim() }],
      }, { withCredentials: true });
      const r = res.data?.results?.[0];
      if (r?.status === 'already_registered') {
        setSingleResult({ type: 'warn', text: `${r.email} ist bereits registriert.` });
      } else if (r?.status === 'already_invited') {
        setSingleResult({ type: 'warn', text: `${r.email} wurde bereits eingeladen.` });
      } else {
        setSingleResult({ type: 'ok', text: 'Einladung erstellt.' });
        setSingleForm({ email: '', first_name: '', last_name: '' });
        loadList();
      }
    } catch (err) {
      setSingleResult({ type: 'err', text: err?.response?.data?.error || 'Fehler beim Einladen.' });
    } finally {
      setSingleLoading(false);
    }
  }

  // ── CSV parsing ──
  function handleFile(file) {
    if (!file) return;
    setCsvResult(null);
    file.text().then(text => {
      const lines = text.replace(/^\uFEFF/, '').split('\n').filter(l => l.trim());
      if (lines.length < 2) { setCsvParsed([]); return; }
      const delimiter = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(delimiter).map(h => h.trim().toLowerCase().replace(/["']/g, ''));
      const emailIdx = headers.indexOf('email');
      const fnIdx = Math.max(headers.indexOf('first_name'), headers.indexOf('vorname'), headers.indexOf('firstname'));
      const lnIdx = Math.max(headers.indexOf('last_name'), headers.indexOf('nachname'), headers.indexOf('lastname'));

      if (emailIdx < 0) {
        setCsvParsed([]);
        setCsvResult({ type: 'err', text: 'CSV enthält keine "email"-Spalte.' });
        return;
      }

      const rows = lines.slice(1).map(line => {
        const cols = line.split(delimiter).map(c => c.trim().replace(/^["']|["']$/g, ''));
        const email = cols[emailIdx] || '';
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return {
          email,
          first_name: fnIdx >= 0 ? (cols[fnIdx] || '') : '',
          last_name: lnIdx >= 0 ? (cols[lnIdx] || '') : '',
          error: !valid ? 'Ungültige E-Mail' : null,
        };
      });
      setCsvParsed(rows);
    });
  }

  async function handleCsvInvite() {
    if (!csvParsed || csvLoading) return;
    const valid = csvParsed.filter(r => !r.error);
    if (valid.length === 0) return;
    setCsvLoading(true);
    setCsvResult(null);
    try {
      const res = await mentoringApi.post('/mpi/invite-employees', {
        employees: valid.map(({ email, first_name, last_name }) => ({ email, first_name, last_name })),
      }, { withCredentials: true });
      const results = res.data?.results || [];
      const invited = results.filter(r => r.status === 'invited').length;
      const skipped = results.filter(r => r.status !== 'invited').length;
      setCsvResult({ type: 'ok', text: `${invited} eingeladen, ${skipped} übersprungen.` });
      setCsvParsed(null);
      if (fileRef.current) fileRef.current.value = '';
      loadList();
    } catch (err) {
      setCsvResult({ type: 'err', text: err?.response?.data?.error || 'Fehler beim Einladen.' });
    } finally {
      setCsvLoading(false);
    }
  }

  function copyLink(inv) {
    const link = `${window.location.origin}/#/ma-register?token=${inv.token}&email=${encodeURIComponent(inv.email)}`;
    navigator.clipboard.writeText(link).catch(() => {});
  }

  // ── Status badge ──
  function StatusBadge({ status }) {
    const map = {
      pending:     { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Ausstehend' },
      registered:  { bg: 'bg-green-500/20',  text: 'text-green-400', label: 'Registriert' },
      expired:     { bg: 'bg-red-500/20',    text: 'text-red-400',   label: 'Abgelaufen' },
    };
    const s = map[status] || map.pending;
    return <span className={`${s.bg} ${s.text} text-[10px] font-semibold px-2 py-0.5 rounded-full`}>{s.label}</span>;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Laden…</div>
      </div>
    );
  }

  const inputCls = 'w-full bg-slate-700 border border-slate-600 rounded-lg p-2.5 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 transition';

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
        <h1 className="text-lg font-bold tracking-tight">Mitarbeiter einladen</h1>
        <button
          onClick={() => navigate('/mpi-dashboard')}
          className="text-sm text-gray-400 hover:text-white transition"
        >
          ← Zurück zum Dashboard
        </button>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

          {/* Card 1: Single invite */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-base font-semibold mb-4">Einzeln einladen</h3>
            <form onSubmit={handleSingleInvite} className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Vorname"
                value={singleForm.first_name}
                onChange={e => setSingleForm(p => ({ ...p, first_name: e.target.value }))}
                className={inputCls}
              />
              <input
                type="text"
                placeholder="Nachname"
                value={singleForm.last_name}
                onChange={e => setSingleForm(p => ({ ...p, last_name: e.target.value }))}
                className={inputCls}
              />
              <input
                type="email"
                placeholder="E-Mail *"
                value={singleForm.email}
                onChange={e => setSingleForm(p => ({ ...p, email: e.target.value }))}
                required
                className={inputCls}
              />
              <button
                type="submit"
                disabled={!singleForm.email.trim() || singleLoading}
                className={`w-full py-2.5 rounded-lg font-semibold text-sm transition ${
                  !singleForm.email.trim() || singleLoading
                    ? 'bg-slate-600 text-gray-400 cursor-not-allowed'
                    : 'bg-amber-500 hover:bg-amber-400 text-black'
                }`}
              >
                {singleLoading ? 'Wird gesendet…' : 'Einladen'}
              </button>
            </form>
            {singleResult && (
              <p className={`text-sm mt-3 ${
                singleResult.type === 'ok' ? 'text-green-400' :
                singleResult.type === 'warn' ? 'text-amber-400' : 'text-red-400'
              }`}>{singleResult.text}</p>
            )}
          </div>

          {/* Card 2: CSV upload */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
            <h3 className="text-base font-semibold mb-4">Mehrere einladen (CSV)</h3>
            <div
              className="border-2 border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-slate-500 transition mb-3"
              onClick={() => fileRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(245,158,11,0.5)'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = ''; }}
              onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = ''; handleFile(e.dataTransfer.files[0]); }}
            >
              <p className="text-sm text-slate-400 mb-1">CSV-Datei hierher ziehen oder klicken</p>
              <p className="text-xs text-slate-500">Spalten: email, first_name, last_name</p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={e => handleFile(e.target.files[0])}
              />
            </div>

            {/* CSV preview */}
            {csvParsed && csvParsed.length > 0 && (
              <div className="mb-3">
                <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-700">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="text-left p-2">E-Mail</th>
                        <th className="text-left p-2">Vorname</th>
                        <th className="text-left p-2">Nachname</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvParsed.map((r, i) => (
                        <tr key={i} className={r.error ? 'text-red-400 bg-red-500/10' : 'text-slate-300'}>
                          <td className="p-2">{r.email || '—'}</td>
                          <td className="p-2">{r.first_name || '—'}</td>
                          <td className="p-2">{r.last_name || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {csvParsed.filter(r => !r.error).length} gültig, {csvParsed.filter(r => r.error).length} fehlerhaft
                </p>
                <button
                  onClick={handleCsvInvite}
                  disabled={csvLoading || csvParsed.filter(r => !r.error).length === 0}
                  className={`w-full mt-2 py-2.5 rounded-lg font-semibold text-sm transition ${
                    csvLoading || csvParsed.filter(r => !r.error).length === 0
                      ? 'bg-slate-600 text-gray-400 cursor-not-allowed'
                      : 'bg-amber-500 hover:bg-amber-400 text-black'
                  }`}
                >
                  {csvLoading ? 'Wird gesendet…' : `Alle einladen (${csvParsed.filter(r => !r.error).length})`}
                </button>
              </div>
            )}
            {csvParsed && csvParsed.length === 0 && !csvResult && (
              <p className="text-xs text-slate-500">Keine gültigen Zeilen gefunden.</p>
            )}
            {csvResult && (
              <p className={`text-sm mt-2 ${csvResult.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                {csvResult.text}
              </p>
            )}
          </div>
        </div>

        {/* Invitation list */}
        <div>
          <h3 className="text-sm font-semibold text-slate-400 mb-3">Eingeladene Mitarbeiter</h3>

          {listLoading ? (
            <p className="text-gray-400 text-sm">Laden…</p>
          ) : invitations.length === 0 ? (
            <p className="text-gray-500 text-sm">Noch keine Mitarbeiter eingeladen.</p>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-400 text-xs border-b border-slate-700">
                      <th className="text-left p-3">Name</th>
                      <th className="text-left p-3">E-Mail</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Eingeladen am</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv, i) => {
                      const name = [inv.first_name, inv.last_name].filter(Boolean).join(' ') || '—';
                      return (
                        <tr key={inv.token || i} className="border-b border-slate-700/50 last:border-0">
                          <td className="p-3 text-slate-300">{name}</td>
                          <td className="p-3 text-slate-300">{inv.email}</td>
                          <td className="p-3"><StatusBadge status={inv.status} /></td>
                          <td className="p-3 text-slate-400 text-xs">{fmtDE(inv.invited_at)}</td>
                          <td className="p-3 text-right">
                            {inv.status === 'pending' && inv.token && (
                              <button
                                onClick={() => copyLink(inv)}
                                className="text-[10px] text-amber-400 hover:text-amber-300 border border-amber-500/30 rounded px-2 py-0.5 transition"
                              >
                                Link kopieren
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
